import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/data`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/**
 * Get list of purchase orders with optional filtering
 */
export async function getPurchaseOrders({ search, status, vendorNo, company, page, pageSize } = {}) {
  const params = {};
  if (search) params.search = search;
  if (status) params.status = status;
  if (vendorNo) params.vendorNo = vendorNo;
  if (company) params.company = company;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;

  const response = await api.get("/orders", { params });
  return response.data;
}

/**
 * Get purchase order by order number (with line details)
 */
export async function getPurchaseOrderById(orderNo) {
  const response = await api.get(`/orders/${encodeURIComponent(orderNo)}`);
  return response.data;
}

/**
 * Get purchase order with all line details
 */
export async function getPurchaseOrderWithLines(orderNo) {
  return getPurchaseOrderById(orderNo);
}

/**
 * Get purchase orders by vendor
 */
export async function getPurchaseOrdersByVendor(vendorNo, options = {}) {
  const response = await api.get(`/orders/vendor/${encodeURIComponent(vendorNo)}`, {
    params: { top: options.top || 50 },
  });
  return response.data;
}

/**
 * Get purchase orders by status
 */
export async function getPurchaseOrdersByStatus(status, options = {}) {
  return getPurchaseOrders({ ...options, status });
}

/**
 * Get open purchase orders
 */
export async function getOpenPurchaseOrders(options = {}) {
  return getPurchaseOrders({ ...options, status: "Open" });
}

/**
 * Get released purchase orders
 */
export async function getReleasedPurchaseOrders(options = {}) {
  return getPurchaseOrders({ ...options, status: "Released" });
}

/**
 * Get purchase orders for a specific item
 */
export async function getPurchaseOrdersByItem(itemNo, options = {}) {
  const orders = await getPurchaseOrders({ ...options, pageSize: 500 });
  // Filter client-side for orders containing this item
  return (orders.rows || []).filter((po) =>
    (po.purchaseOrderLines || []).some((line) => line.itemNumber === itemNo)
  );
}

/**
 * Get purchase order lines for a specific order
 */
export async function getPurchaseOrderLines(orderNo) {
  const order = await getPurchaseOrderById(orderNo);
  return order.purchaseOrderLines || [];
}

/**
 * Get all PO lines for a specific item across all orders
 */
export async function getPOLinesByItem(itemNo) {
  const response = await api.get(`/orders/item/${encodeURIComponent(itemNo)}/lines`);
  return response.data;
}

/**
 * Calculate purchase order totals
 */
export async function calculatePurchaseOrderTotals(orderNo) {
  const lines = await getPurchaseOrderLines(orderNo);

  const subtotal = lines.reduce((sum, line) => sum + (line.amountExcludingTax || 0), 0);
  const tax = lines.reduce((sum, line) => sum + (line.totalTaxAmount || 0), 0);
  const total = lines.reduce((sum, line) => sum + (line.amountIncludingTax || line.amountExcludingTax || 0), 0);

  return { subtotal, tax, total, lineCount: lines.length };
}
